import { useCallback, useEffect, useState } from "react";
import { adminApi, getApiErrorMessage } from "@/lib/api";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Database, FlaskConical, PlayCircle, PlugZap, RefreshCw } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";

const STATUS_STYLE = {
  PASS: "bg-emerald-600", REACHED_VALIDATION: "bg-amber-600", AUTHZ_GAP: "bg-orange-600",
  ENDPOINT_GAP: "bg-slate-600", FAIL: "bg-red-600", PARTIAL: "bg-amber-600",
};

function StepRow({ result }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-[var(--glass-border)] p-3">
      <button type="button" className="flex w-full items-center justify-between gap-3 text-left" onClick={() => setOpen(!open)}>
        <div className="flex min-w-0 items-center gap-3">
          <Badge className={STATUS_STYLE[result.status] || "bg-slate-600"}>{result.status || "UNKNOWN"}</Badge>
          <span className="truncate text-sm font-medium">{result.step || result.endpoint}</span>
        </div>
        <span className="shrink-0 text-xs text-[var(--ink-muted)]">{result.latency_ms != null ? `${result.latency_ms} ms` : ""}</span>
      </button>
      {result.note && <p className="mt-2 text-xs text-amber-800">{result.note}</p>}
      {open && <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap rounded bg-slate-950 p-3 text-xs text-emerald-200">{JSON.stringify(result.response || result.error || result, null, 2)}</pre>}
    </div>
  );
}

function RunSummary({ run }) {
  if (!run) return null;
  const results = run.steps || run.results || [];
  return (
    <div className="mt-5 space-y-2" data-testid="bse2-harness-results">
      <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
        <span>{run.evidence?.run_id || run.suite || "Run"}</span>
        <Badge className="bg-emerald-600">{run.passed || 0} passed</Badge>
        {!!run.gaps && <Badge className="bg-amber-600">{run.gaps} exchange/prerequisite gaps</Badge>}
        {!!run.failed && <Badge className="bg-red-600">{run.failed} failed</Badge>}
        {!!run.pending_2fa?.length && <Badge variant="outline">{run.pending_2fa.length} investor approvals pending</Badge>}
      </div>
      {results.map((result, index) => <StepRow key={`${result.step || result.endpoint}-${index}`} result={result} />)}
    </div>
  );
}

export default function Bse2Console() {
  const { admin } = useAdminAuth();
  const isSuper = admin?.role === "super_admin";
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [webhooks, setWebhooks] = useState(null);
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState("");
  const [uat, setUat] = useState({ ucc: "", confirmation: "" });

  const load = useCallback(async () => {
    const [s, h, w, r] = await Promise.all([
      adminApi.get("/admin/bse2/status"), adminApi.get("/admin/bse2/health"),
      adminApi.get("/admin/bse2/webhook-events"), adminApi.get("/admin/bse2/cert/runs", { params: { limit: 10 } }),
    ]);
    setStatus(s.data); setHealth(h.data); setWebhooks(w.data); setRuns(r.data.runs || []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const action = async (key, request, success) => {
    setBusy(key);
    try {
      const { data } = await request();
      if (data.status === "error") throw Object.assign(new Error(data.error?.message || data.message || "BSE rejected the operation"), { response: { data: { detail: data.error || data.message } } });
      if (data.steps || data.results) setRun(data);
      const message = typeof success === "function" ? success(data) : success;
      if (Number(data.failed || 0) > 0) toast.error(message);
      else if (Number(data.gaps || 0) > 0) toast.warning(message);
      else toast.success(message);
      await load();
      return data;
    } catch (error) {
      toast.error(getApiErrorMessage(error));
      return null;
    } finally {
      setBusy("");
    }
  };

  const fullUat = () => action(
    "full-uat",
    () => adminApi.post("/admin/bse2/uat/full", {
      existing_physical_ucc: uat.ucc.trim(), confirmation: uat.confirmation,
    }, { timeout: 300_000 }),
    (data) => `Full BSE UAT completed: ${data.passed} passed, ${data.gaps} explicit gaps, ${data.failed} failures`,
  );

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl text-[var(--brand)]" data-testid="bse2-console-title">BSE StAR MF 2.0</h1>
          <p className="mt-1 text-sm text-[var(--ink-muted)]">Real exchange connectivity, UAT certification evidence and reconciliation. No simulated BSE state.</p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={() => action("refresh", async () => { await load(); return { data: {} }; }, "BSE status refreshed")} disabled={!!busy}>
          <RefreshCw className={`mr-2 h-4 w-4 ${busy === "refresh" ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><PlugZap className="h-4 w-4 text-[var(--cta)]" /> Connection</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {!status ? <p className="text-[var(--ink-muted)]">Checking BSE…</p> : <>
              <div className="flex justify-between"><span>Configured</span><Badge className={status.configured ? "bg-emerald-600" : "bg-red-600"}>{status.configured ? "YES" : "NO"}</Badge></div>
              <div className="flex justify-between gap-3"><span>Member</span><span>{status.member_code} · {status.mode}</span></div>
              <div className="flex justify-between gap-3"><span>Regular schemes</span><span>{status.bse_scheme_count}</span></div>
              <div className="truncate text-xs text-[var(--ink-muted)]" title={status.base_url}>{status.base_url}</div>
            </>}
          </CardContent>
        </Card>
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="flex items-center gap-2 text-base">{health?.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />} Transaction health</CardTitle></CardHeader>
          <CardContent>
            {!health ? <p className="text-sm text-[var(--ink-muted)]">Loading transaction health…</p> : <>
              <div className="grid gap-3 text-sm sm:grid-cols-3">
                <div className="rounded-lg bg-[var(--surface-2)] p-3"><div className="text-xs text-[var(--ink-muted)]">Failed, last 24h</div><div className="text-xl font-semibold">{health.failed_last_24h}</div></div>
                <div className="rounded-lg bg-[var(--surface-2)] p-3"><div className="text-xs text-[var(--ink-muted)]">Webhooks, last 24h</div><div className="text-xl font-semibold">{health.webhook_events_last_24h}</div></div>
                <div className="rounded-lg bg-[var(--surface-2)] p-3"><div className="text-xs text-[var(--ink-muted)]">Last reconciliation</div><div className="truncate text-xs font-medium">{health.last_reconcile_run?.at?.replace("T", " ").slice(0, 19) || "Never"}</div></div>
              </div>
              {(health.alerts || []).map((message) => <div key={message} className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">{message}</div>)}
              <Button size="sm" variant="outline" className="mt-3 rounded-full" disabled={!!busy}
                onClick={() => action("reconcile", () => adminApi.post("/admin/bse2/reconcile-now"), (d) => `Reconciliation complete: ${d.checked || 0} checked`)}>
                <RefreshCw className={`mr-1 h-3.5 w-3.5 ${busy === "reconcile" ? "animate-spin" : ""}`} /> Reconcile now
              </Button>
            </>}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><PlayCircle className="h-4 w-4 text-[var(--cta)]" /> Safe diagnostics</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button className="rounded-full bg-[var(--cta)]" disabled={!!busy}
              onClick={() => action("read", () => adminApi.post("/admin/bse2/cert/read"), (d) => `Read certification: ${d.passed} passed, ${d.gaps || 0} gaps`)}>
              {busy === "read" && <RefreshCw className="mr-2 h-4 w-4 animate-spin" />} Run read certification
            </Button>
            <Button variant="outline" className="rounded-full" disabled={!!busy}
              onClick={() => action("matrix", () => adminApi.post("/admin/bse2/connectivity-matrix"), (d) => `Connectivity: ${d.reached}/${d.total} endpoints reached`)}>
              <PlugZap className="mr-2 h-4 w-4" /> Connectivity matrix
            </Button>
            <Button variant="outline" className="rounded-full" disabled={!!busy}
              onClick={() => action("schemes", () => adminApi.post("/admin/bse2/scheme-sync"), (d) => `Synced ${d.synced} Regular BSE schemes`)}>
              <Database className="mr-2 h-4 w-4" /> Sync Regular scheme master
            </Button>
          </div>
          <p className="mt-3 text-xs text-[var(--ink-muted)]">These calls read BSE and local status only. Scheme sync enforces the Regular-only policy and rejects Direct-plan rows.</p>
          <RunSummary run={run} />
        </CardContent>
      </Card>

      {isSuper && <Card className="mt-6 border-amber-300">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><FlaskConical className="h-4 w-4 text-amber-700" /> Full real UAT transaction suite</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-950">
            Creates real records in BSE UAT: physical/demat UCCs, purchase, redemption, switch, SIP, XSIP, STP, SWP and mandates. It is hard-blocked in LIVE. Investor authentication remains a real manual BSE step.
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5"><Label>Existing BSE-verified physical UCC</Label><Input value={uat.ucc} onChange={(e) => setUat((v) => ({ ...v, ucc: e.target.value.toUpperCase() }))} placeholder="Verified UAT UCC" /></div>
            <div className="space-y-1.5"><Label>Type RUN FULL UAT</Label><Input value={uat.confirmation} onChange={(e) => setUat((v) => ({ ...v, confirmation: e.target.value }))} placeholder="RUN FULL UAT" /></div>
          </div>
          <Button className="rounded-full bg-amber-700 hover:bg-amber-800" disabled={!!busy || !uat.ucc || uat.confirmation !== "RUN FULL UAT"} onClick={fullUat}>
            {busy === "full-uat" ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />}
            {busy === "full-uat" ? "Running real BSE UAT…" : "Run full UAT suite"}
          </Button>
        </CardContent>
      </Card>}

      <Card className="mt-6">
        <CardHeader><CardTitle className="text-base">Recent certification runs</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {runs.length === 0 ? <p className="text-sm text-[var(--ink-muted)]">No certification evidence recorded.</p> : runs.map((item) => (
            <button type="button" key={item.id || item.at} onClick={() => setRun(item)} className="flex w-full flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--glass-border)] p-3 text-left text-sm hover:bg-[var(--surface-2)]">
              <span><b>{item.evidence?.run_id || item.suite}</b> · {item.at?.replace("T", " ").slice(0, 19)} · {item.run_by}</span>
              <span>{item.passed || 0} passed · {item.gaps || 0} gaps · {item.failed || 0} failed</span>
            </button>
          ))}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader><CardTitle className="text-base">BSE webhook events</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            {(webhooks?.recent_events || []).slice(0, 15).map((event, index) => (
              <div key={`${event.received_at}-${index}`} className="flex flex-wrap justify-between gap-2 rounded border border-[var(--glass-border)] px-3 py-2 text-xs">
                <span><b>{event.event_type}</b> · {event.event || "status update"} {event.client_code ? `· ${event.client_code}` : ""}</span>
                <span className="text-[var(--ink-muted)]">{event.received_at?.replace("T", " ").slice(0, 19)}</span>
              </div>
            ))}
            {!webhooks?.recent_events?.length && <p className="text-sm text-[var(--ink-muted)]">No BSE webhook events received yet.</p>}
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
